// The generic id mechanism (prefix format, generation, validation) is part of
// the published contract and lives in `@runloop/reflex-contract`. The registry
// of core entity kinds below stays here: it is this application's set, not
// something an external client declares.
import { BODY_LEN, createIdFactory } from '@runloop/reflex-contract';
import { z } from 'zod';

/** Prefix map for entities owned by the core server (non-plugin). */
export const CORE_IDS = {
  agent: 'agt',
  user: 'usr',
  billingEntity: 'ent',
  realmAccountLinkGrant: 'rlg',
  organization: 'org',
  organizationMember: 'orm',
  organizationEmailDomain: 'oed',
  team: 'tem',
  teamMember: 'tmm',
  role: 'rol',
  providerAccount: 'pac',
  authIdentity: 'aid',
  emailVerificationToken: 'evt',
  repoConfig: 'rpc',
  accountFeatureFlag: 'aff',
  resourceGrant: 'rsg',
  shareRequest: 'srq',
  agentMessage: 'msg',
  organizationInvite: 'inv',
  secret: 'sec',
  modelProviderSecret: 'mps',
  llmTaskAssignment: 'lta',
  agentDraft: 'agd',
  agentAssociation: 'aas',
  agentGroup: 'agp',
  agentLabel: 'alb',
  personalApiKey: 'pak',
  serviceAccount: 'sva',
  teardownLeftover: 'tdl',
  effectReceipt: 'efr',
  agentCommand: 'acm',
  agentLease: 'agl',
  idpConfig: 'idp',
  idpGroup: 'idg',
  idpGroupMember: 'igm',
  agentListener: 'lsn',
  agentCapabilityGrant: 'acg',
  agentActionRequest: 'aar',
  domainClaim: 'dmc',
} as const;

export type CoreIdKind = keyof typeof CORE_IDS;

/**
 * The typed form of a core entity id: a string whose prefix is checked at
 * compile time (`CoreId<'user'>` = `` `usr_${string}` ``). Use this — not a
 * bare `string` — wherever a contract means "an id of THIS kind", so a
 * user id cannot be handed where a role id is expected. Values narrow to
 * it by parsing at the boundary: {@link coreIdSchema} for Zod contracts,
 * {@link asCoreId} for imperative code.
 */
export type CoreId<K extends CoreIdKind> = `${(typeof CORE_IDS)[K]}_${string}`;

const coreIdFactory = createIdFactory(CORE_IDS);

/** The application's id factory, with `generate` returning the typed {@link CoreId}. */
type CoreIdFactory = Omit<typeof coreIdFactory, 'generate'> & {
  generate<K extends CoreIdKind>(kind: K): CoreId<K>;
};

/**
 * The core id factory, with `generate` narrowed to return the typed
 * {@link CoreId} for the requested kind (the factory itself is
 * application-agnostic and types ids as `string`). Runtime behavior is
 * the factory's, unchanged.
 */
export const coreIds: CoreIdFactory = coreIdFactory as CoreIdFactory;

/**
 * `coreIds.schema(kind)` with its inferred type narrowed to
 * {@link CoreId}. Runtime behavior is identical (the same prefix-checked
 * `ZodString`); only the inferred TypeScript type tightens.
 */
export function coreIdSchema<K extends CoreIdKind>(kind: K): z.ZodType<CoreId<K>> {
  return coreIds.schema(kind) as unknown as z.ZodType<CoreId<K>>;
}

/**
 * Narrow a runtime string to a {@link CoreId}, throwing on any other
 * shape. The boundary parser for ids that arrive untyped (request
 * context, environment) — prefer it over an `as` cast so a wrong id kind
 * fails loudly where it enters instead of deep in a query.
 */
export function asCoreId<K extends CoreIdKind>(kind: K, value: string): CoreId<K> {
  if (!coreIds.isOf(kind, value)) {
    throw new Error(`Expected a ${kind} id (${CORE_IDS[kind]}_…), got "${value}"`);
  }
  return value as CoreId<K>;
}

/**
 * Any prefixed entity id — core- or plugin-owned. The static type checks
 * only the `<prefix>_<body>` shape (a plugin's prefix isn't knowable
 * here); pair it with {@link EntityIdSchema} at the boundary for the full
 * format check.
 */
export type EntityId = `${string}_${string}`;

/** Matches any well-formed prefixed entity id (`abc_` + 22 base62 chars). */
export const ENTITY_ID_RE = new RegExp(`^[a-z]{3}_[A-Za-z0-9]{${BODY_LEN}}$`);

/** Zod boundary check for {@link EntityId}: any well-formed prefixed entity id. */
export const EntityIdSchema = z
  .string()
  .regex(
    ENTITY_ID_RE,
    'Expected a prefixed entity id like abc_XXXXXXXXXXXXXXXXXXXXXX',
  ) as unknown as z.ZodType<EntityId>;
